import { useEffect, useState } from "react";
import {
  Plus, FileText, Link as LinkIcon, Copy, Eye, Download, Trash2, RefreshCw,
  CheckCircle2, PenLine, Sparkles, Loader2, ArrowLeft
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { authFetch, API } from "../utils/api";
import { AgreementBody, ReadModeToggle } from "../pages/PublicSignAgreementPage";
import { SignaturePad } from "./SignaturePad";

const PLAN_OPTIONS = [
  { value: "self_managed", label: "Self-managed" },
  { value: "plan_managed", label: "Plan-managed" },
  { value: "ndia_managed", label: "NDIA-managed" },
  { value: "private", label: "Privately funded (non-NDIS)" },
];

const EMPTY = {
  participant_name: "", participant_email: "", participant_phone: "",
  date_of_birth: "", address: "", ndis_number: "", ndis_plan_type: "plan_managed",
  plan_manager_name: "", plan_manager_email: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  start_date: "", end_date: "", services_summary: "", notes: "", linked_user_id: "",
};

export function AdminServiceAgreementBuilder() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);       // draft being previewed/signed
  const [editing, setEditing] = useState(null);         // draft being edited (or "new")
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [participants, setParticipants] = useState([]);

  const load = async () => {
    setLoading(true);
    const [dRes, uRes] = await Promise.all([
      authFetch(`${API}/admin/service-agreement-drafts`),
      authFetch(`${API}/admin/users?limit=500`),
    ]);
    if (dRes.ok) setDrafts(await dRes.json());
    if (uRes.ok) {
      const u = await uRes.json();
      const list = Array.isArray(u) ? u : u.users || [];
      setParticipants(list.filter((x) => x.user_type === "participant"));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setEditing("new"); };

  const startEdit = (d) => {
    setForm({ ...EMPTY, ...d });
    setEditing(d.draft_id);
  };

  const linkParticipant = (userId) => {
    if (!userId) {
      setForm((f) => ({ ...f, linked_user_id: "" }));
      return;
    }
    const p = participants.find((x) => x.user_id === userId);
    if (!p) return;
    setForm((f) => ({
      ...f,
      linked_user_id: p.user_id,
      participant_name: p.name || f.participant_name,
      participant_email: p.email || f.participant_email,
      participant_phone: p.phone || f.participant_phone,
      date_of_birth: p.dob || f.date_of_birth,
      address: p.address || f.address,
      ndis_number: p.ndis_number || f.ndis_number,
      ndis_plan_type: p.ndis_plan_type || f.ndis_plan_type,
      plan_manager_name: p.plan_manager_name || f.plan_manager_name,
      plan_manager_email: p.plan_manager_email || f.plan_manager_email,
      emergency_contact_name: p.emergency_contact_name || f.emergency_contact_name,
      emergency_contact_phone: p.emergency_contact_phone || f.emergency_contact_phone,
    }));
  };

  const save = async () => {
    if (!form.participant_name?.trim()) {
      return toast.error("Participant name is required");
    }
    setSaving(true);
    try {
      const url = editing === "new"
        ? `${API}/admin/service-agreement-drafts`
        : `${API}/admin/service-agreement-drafts/${editing}`;
      const method = editing === "new" ? "POST" : "PUT";
      const r = await authFetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        toast.success(editing === "new" ? "Draft created" : "Draft updated");
        setEditing(null);
        await load();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this draft?")) return;
    const r = await authFetch(`${API}/admin/service-agreement-drafts/${id}`, { method: "DELETE" });
    if (r.ok) { toast.success("Deleted"); await load(); }
    else toast.error("Couldn't delete");
  };

  const copyLink = (draft) => {
    const url = `${window.location.origin}/sign-agreement/${draft.sign_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Signing link copied to clipboard");
  };

  const downloadPdf = async (draft) => {
    const r = await authFetch(`${API}/admin/service-agreement-drafts/${draft.draft_id}/pdf`);
    if (!r.ok) return toast.error("PDF not available (has it been signed?)");
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `BTR-Service-Agreement-${draft.participant_name || "participant"}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  // ─── VIEWS ────────────────────────────────────────────────────────────

  if (selected) {
    return (
      <InPersonSignView
        draft={selected}
        onBack={() => { setSelected(null); load(); }}
        onCopyLink={copyLink}
      />
    );
  }

  if (editing) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6" data-testid="agreement-draft-form">
        <div className="flex items-center justify-between mb-4">
          <div>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 mb-1"
            >
              <ArrowLeft className="w-3 h-3" /> Back
            </button>
            <h3 className="font-semibold text-slate-900">
              {editing === "new" ? "New Service Agreement draft" : "Edit draft"}
            </h3>
          </div>
          <Button onClick={save} disabled={saving} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="draft-save-btn">
            {saving ? "Saving…" : (editing === "new" ? "Create draft" : "Save changes")}
          </Button>
        </div>

        <div className="mb-4">
          <Label className="text-xs">Link to an existing participant (optional)</Label>
          <select
            value={form.linked_user_id || ""}
            onChange={(e) => linkParticipant(e.target.value)}
            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            data-testid="draft-link-participant"
          >
            <option value="">— none — (external / new signup)</option>
            {participants.map((p) => (
              <option key={p.user_id} value={p.user_id}>{p.name} ({p.email})</option>
            ))}
          </select>
          <p className="text-[11px] text-slate-400 mt-1">
            Linking auto-fills details and marks the participant&apos;s service agreement as approved once signed.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Participant name *" value={form.participant_name} onChange={(v) => setForm({ ...form, participant_name: v })} testId="draft-name" />
          <Field label="Email" value={form.participant_email} onChange={(v) => setForm({ ...form, participant_email: v })} testId="draft-email" />
          <Field label="Phone" value={form.participant_phone} onChange={(v) => setForm({ ...form, participant_phone: v })} testId="draft-phone" />
          <Field label="Date of birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} testId="draft-dob" />
          <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="draft-address" className="md:col-span-2" />
          <Field label="NDIS number" value={form.ndis_number} onChange={(v) => setForm({ ...form, ndis_number: v })} testId="draft-ndis" />
          <div>
            <Label className="text-xs">NDIS plan type</Label>
            <select
              value={form.ndis_plan_type}
              onChange={(e) => setForm({ ...form, ndis_plan_type: e.target.value })}
              className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="draft-plan-type"
            >
              {PLAN_OPTIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
          <Field label="Plan manager name" value={form.plan_manager_name} onChange={(v) => setForm({ ...form, plan_manager_name: v })} testId="draft-pm-name" />
          <Field label="Plan manager email" value={form.plan_manager_email} onChange={(v) => setForm({ ...form, plan_manager_email: v })} testId="draft-pm-email" />
          <Field label="Emergency contact name" value={form.emergency_contact_name} onChange={(v) => setForm({ ...form, emergency_contact_name: v })} testId="draft-ec-name" />
          <Field label="Emergency contact phone" value={form.emergency_contact_phone} onChange={(v) => setForm({ ...form, emergency_contact_phone: v })} testId="draft-ec-phone" />
          <Field label="Start date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testId="draft-start" />
          <Field label="End date" type="date" value={form.end_date} onChange={(v) => setForm({ ...form, end_date: v })} testId="draft-end" />
        </div>

        <div className="mt-3">
          <Label className="text-xs">Services summary</Label>
          <Textarea
            value={form.services_summary || ""}
            onChange={(e) => setForm({ ...form, services_summary: e.target.value })}
            placeholder="e.g. Community access (weekdays and weekends), Personal care (Mon/Wed/Fri), Assist with household tasks"
            rows={3}
            className="mt-1"
            data-testid="draft-services"
          />
        </div>

        <div className="mt-3">
          <Label className="text-xs">Internal notes (not shown on the agreement)</Label>
          <Textarea
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={2}
            className="mt-1"
            data-testid="draft-notes"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-agreement-builder">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#4FA3C7]" /> Service Agreement drafts
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Manually build an agreement, share the signing link with a participant, or capture their signature in person.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="drafts-refresh"><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <Button size="sm" onClick={startNew} className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white" data-testid="draft-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New draft
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left p-3 font-medium">Participant</th>
              <th className="text-left p-3 font-medium">Plan</th>
              <th className="text-left p-3 font-medium">Status</th>
              <th className="text-left p-3 font-medium">Updated</th>
              <th className="text-left p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={5} className="p-6 text-center text-slate-400">Loading…</td></tr>)}
            {!loading && drafts.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-400">
                No drafts yet. Click <strong>New draft</strong> above to create your first Service Agreement.
              </td></tr>
            )}
            {drafts.map((d) => (
              <tr key={d.draft_id} className="border-t" data-testid={`draft-row-${d.draft_id}`}>
                <td className="p-3">
                  <div className="font-medium text-slate-800">{d.participant_name}</div>
                  <div className="text-xs text-slate-400">{d.participant_email || "no email"}</div>
                </td>
                <td className="p-3 text-slate-600 text-xs">
                  {d.ndis_plan_type ? PLAN_OPTIONS.find((o) => o.value === d.ndis_plan_type)?.label || d.ndis_plan_type : "—"}
                </td>
                <td className="p-3">
                  {d.status === "signed"
                    ? <Badge className="bg-emerald-100 text-emerald-700 border-0"><CheckCircle2 className="w-3 h-3 mr-1" />Signed</Badge>
                    : <Badge className="bg-amber-100 text-amber-700 border-0">Draft</Badge>
                  }
                </td>
                <td className="p-3 text-xs text-slate-500">
                  {new Date(d.updated_at || d.created_at).toLocaleDateString("en-AU")}
                </td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1">
                    {d.status !== "signed" && (
                      <>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => startEdit(d)} data-testid={`draft-edit-${d.draft_id}`}>
                          <PenLine className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => copyLink(d)} data-testid={`draft-copy-${d.draft_id}`}>
                          <Copy className="w-3 h-3 mr-1" /> Copy link
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSelected(d)} data-testid={`draft-sign-${d.draft_id}`}>
                          <PenLine className="w-3 h-3 mr-1" /> Sign in person
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => remove(d.draft_id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    {d.status === "signed" && (
                      <>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSelected(d)} data-testid={`draft-view-${d.draft_id}`}>
                          <Eye className="w-3 h-3 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => downloadPdf(d)} data-testid={`draft-pdf-${d.draft_id}`}>
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", className = "", testId }) {
  return (
    <div className={className}>
      <Label className="text-xs">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1" data-testid={testId} />
    </div>
  );
}

/**
 * In-person signing view — admin opens this on the same device and passes it
 * to the participant to sign. Also used to view a signed agreement.
 */
function InPersonSignView({ draft, onBack, onCopyLink }) {
  const [readMode, setReadMode] = useState("normal");
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [signedName, setSignedName] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(draft.status === "signed");

  const sign = async () => {
    const expected = (draft.participant_name || "").trim().toLowerCase();
    if (!signedName.trim() || signedName.trim().toLowerCase() !== expected) {
      return toast.error("Typed name must match the participant's name exactly");
    }
    if (!signatureDataUrl) return toast.error("Please draw the signature");
    if (!agreed) return toast.error("Please tick agreement before signing");
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/sign-agreement/${draft.sign_token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ signed_name: signedName, agreed: true, signature_data_url: signatureDataUrl }),
      });
      if (r.ok) {
        toast.success("Agreement signed");
        setDone(true);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || "Signing failed");
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid="in-person-sign-view">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to drafts
        </button>
        <ReadModeToggle mode={readMode} onChange={setReadMode} />
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
        <h3 className="text-xl font-bold text-slate-900 mb-1">
          NDIS Service Agreement
        </h3>
        <p className="text-sm text-slate-500 mb-4">
          For <strong className="text-slate-700">{draft.participant_name}</strong>
          {draft.ndis_number && <> · NDIS {draft.ndis_number}</>}
        </p>
        <AgreementBody draft={draft} readMode={readMode} onReadToBottom={() => setScrolledToEnd(true)} />
      </div>

      {done ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-2" />
          <p className="font-semibold text-slate-800">Agreement is signed.</p>
          <p className="text-sm text-slate-500 mb-3">
            {draft.signed_at ? `Signed at ${new Date(draft.signed_at).toLocaleString("en-AU")}` : "Signed just now"}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
          <div className="flex items-start gap-2 mb-4">
            <input
              type="checkbox"
              id="ip-agreed"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              disabled={!scrolledToEnd}
              className="mt-1"
              data-testid="ip-agreement-checkbox"
            />
            <label htmlFor="ip-agreed" className="text-sm text-slate-700">
              I have read this Service Agreement and I agree to it.
              {!scrolledToEnd && (
                <span className="block text-xs text-amber-600 mt-1">
                  Please scroll to the bottom of the agreement first.
                </span>
              )}
            </label>
          </div>

          <div className="mb-4">
            <Label className="text-xs">Type your full legal name *</Label>
            <Input
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              placeholder={`Type "${draft.participant_name}" exactly`}
              className="h-12 mt-1 font-serif italic text-lg"
              data-testid="ip-agreement-name"
            />
          </div>

          <div className="mb-4">
            <Label className="text-xs">Draw signature *</Label>
            <div className="mt-1">
              <SignaturePad onChange={setSignatureDataUrl} height={180} />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={sign}
              disabled={!agreed || submitting}
              className="bg-[#87CEEB] hover:bg-[#6BB8DB] text-white"
              data-testid="ip-sign-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Sign now
            </Button>
            <Button variant="outline" onClick={() => onCopyLink(draft)}>
              <LinkIcon className="w-4 h-4 mr-1" /> Copy signing link instead
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
